/** Angle (facet) membership helpers — facets 1c-2. Pure; no React. */
import { plainText } from "@/lib/conceptGuidance";
import { inYearRange } from "@/lib/setup/preview";
import type { ConceptScope, FacetSummary } from "@/types/concept";
import type { PaperWithRole } from "@/types/paper";

/** Provisional (spec §5); the owner membership gate (§6) sets the final values. */
export const ANGLE_MEMBER_MIN = 40;
export const ANGLE_BRIDGE_MIN = 70;
/** `angle` URL value for the Bridges tab. */
export const BRIDGES = "bridges";
/** Spec: review when the label changed or the question changed by > ~30%. */
export const REVIEW_CHANGE_FRACTION = 0.3;

type FacetLike = Pick<FacetSummary, "id" | "label" | "scope">;
type Scored = Pick<PaperWithRole, "angle_scores" | "year">;
type Overall = Pick<PaperWithRole, "user_score_override" | "normalized_score">;

export interface AngleChip { id: string; label: string; score: number }

/** The overall as the Score column shows it. */
export function overallScore(p: Overall): number {
  return p.user_score_override ?? p.normalized_score ?? 0;
}

export function angleScore(p: Pick<PaperWithRole, "angle_scores">, facetId: string): number | undefined {
  return p.angle_scores?.[facetId];
}

/** Spec §3: a facet's own (non-null) keys over the concept's. */
export function effectiveScope(facet: Pick<FacetSummary, "scope">, conceptScope: ConceptScope | null | undefined): ConceptScope {
  const own = Object.fromEntries(Object.entries(facet.scope ?? {}).filter(([, v]) => v != null));
  return { ...(conceptScope ?? {}), ...own };
}

function scoreIn(p: Scored, facet: FacetLike, conceptScope: ConceptScope | null | undefined): number | undefined {
  const s = p.angle_scores?.[facet.id];
  if (s === undefined) return undefined;
  return inYearRange(p.year, effectiveScope(facet, conceptScope)) ? s : undefined;
}

/** Angle tab membership: >= 40 on it and inside its year range. */
export function isAngleMember(p: Scored, facet: FacetLike, conceptScope: ConceptScope | null | undefined): boolean {
  const s = scoreIn(p, facet, conceptScope);
  return s !== undefined && s >= ANGLE_MEMBER_MIN;
}

/** >= 70 on 2+ active in-range angles AND overall >= 70. Not ConceptPaper.is_bridge (Field Debate). */
export function isAngleBridge(p: Scored & Overall, facets: FacetLike[], conceptScope: ConceptScope | null | undefined): boolean {
  if (!p.angle_scores || overallScore(p) < ANGLE_BRIDGE_MIN) return false;
  let high = 0;
  for (const f of facets) {
    const s = scoreIn(p, f, conceptScope);
    if (s !== undefined && s >= ANGLE_BRIDGE_MIN) high += 1;
  }
  return high >= 2;
}

/** {facetId: members, bridges: n}. */
export function angleCounts(
  papers: (Scored & Overall)[],
  facets: FacetLike[],
  conceptScope: ConceptScope | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = Object.fromEntries(facets.map((f) => [f.id, 0]));
  out[BRIDGES] = 0;
  for (const p of papers) {
    for (const f of facets) if (isAngleMember(p, f, conceptScope)) out[f.id] += 1;
    if (isAngleBridge(p, facets, conceptScope)) out[BRIDGES] += 1;
  }
  return out;
}

/** Best active angle (>= 40) for the badge. Argmax in position order; ties keep
 * the lower position (backend rule, facet_scores.py). `facets` is position-ordered. */
export function bestAngle(p: Pick<PaperWithRole, "angle_scores">, facets: Pick<FacetSummary, "id" | "label">[]): AngleChip | null {
  let best: AngleChip | null = null;
  for (const f of facets) {
    const s = p.angle_scores?.[f.id];
    if (s !== undefined && s >= ANGLE_MEMBER_MIN && (!best || s > best.score)) best = { id: f.id, label: f.label, score: s };
  }
  return best;
}

/** "Why it's here" chips: every scored active angle, facet order. */
export function angleChips(p: Pick<PaperWithRole, "angle_scores">, facets: Pick<FacetSummary, "id" | "label">[]): AngleChip[] {
  if (!p.angle_scores) return [];
  return facets.flatMap((f) => {
    const score = p.angle_scores?.[f.id];
    return score === undefined ? [] : [{ id: f.id, label: f.label, score }];
  });
}

export function seedCountsByFacet(seeds: Pick<PaperWithRole, "seed_facet_ids">[], facets: Pick<FacetSummary, "id">[]): Record<string, number> {
  const out: Record<string, number> = Object.fromEntries(facets.map((f) => [f.id, 0]));
  for (const s of seeds) for (const id of s.seed_facet_ids ?? []) if (id in out) out[id] += 1;
  return out;
}

/** The URL `angle` value if it still means something (2+ active angles), else undefined. */
export function validAngle(value: string | undefined, facets: Pick<FacetSummary, "id">[]): string | undefined {
  if (!value || facets.length < 2) return undefined;
  if (value === BRIDGES || facets.some((f) => f.id === value)) return value;
  return undefined;
}

/** Edit distance / longer length over plain text (0 = same, 1 = all new). */
export function changedFraction(before: string, after: string): number {
  const a = plainText(before);
  const b = plainText(after);
  const n = Math.max(a.length, b.length);
  if (n === 0) return 0;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length] / n;
}
